import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardActions,
    IconButton,
    useTheme,
    alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useSelector, useDispatch } from 'react-redux';
import { toggleFavorite } from '../../Modules/Common/Auth';
import { setSelectedTool } from '../../Modules/Tools/Redux/Actions';
import { setActiveMenu } from '../../Modules/Common/App';

const ToolCardStyled = styled(Card, {
    shouldForwardProp: (prop) => prop !== 'available',
})(({ theme, available }) => {
    const primaryColor = theme.palette.primary.main;
    const isDarkMode = theme.palette.mode === 'dark';
    const mobile = theme.breakpoints.down('sm');

    return {
        background: theme.palette.background.paper,
        borderRadius: theme.spacing(1.5), // Slightly more rounded for modern look
        padding: 0,
        // Google Material Design / Microsoft Fluent style shadow - neutral, subtle
        boxShadow: isDarkMode
            ? `0 2px 4px ${alpha(theme.palette.common.black, 0.4)}, 0 1px 2px ${alpha(theme.palette.common.black, 0.3)}`
            : `0 1px 2px ${alpha(theme.palette.common.black, 0.08)}, 0 2px 4px ${alpha(theme.palette.common.black, 0.06)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', // Material 3 easing
        // Subtle border - Google/Microsoft style
        border: `1px solid ${isDarkMode
            ? alpha(theme.palette.common.white, 0.08)
            : alpha(theme.palette.common.black, 0.04)}`,
        cursor: available ? 'pointer' : 'not-allowed',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        height: '100%',
        '&::before': available ? {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${primaryColor}, ${alpha(primaryColor, 0.6)})`,
            opacity: 0,
            transition: 'opacity 0.3s ease',
        } : {},
        '&:hover': available ? {
            // Google Material Design elevation - neutral gray shadow, no color tint
            boxShadow: isDarkMode
                ? `0 8px 16px ${alpha(theme.palette.common.black, 0.5)}, 0 4px 8px ${alpha(theme.palette.common.black, 0.4)}, 0 2px 4px ${alpha(theme.palette.common.black, 0.3)}`
                : `0 4px 8px ${alpha(theme.palette.common.black, 0.12)}, 0 2px 4px ${alpha(theme.palette.common.black, 0.08)}, 0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
            transform: 'translateY(-2px)', // Subtle lift like Google/Microsoft
            borderColor: isDarkMode
                ? alpha(theme.palette.common.white, 0.12)
                : alpha(theme.palette.common.black, 0.08),
            '&::before': {
                opacity: 1,
            },
            '& .icon-wrapper': {
                transform: 'scale(1.1) rotate(5deg)',
                // Subtle shadow on icon wrapper
                boxShadow: isDarkMode
                    ? `0 2px 8px ${alpha(theme.palette.common.black, 0.4)}`
                    : `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
            },
            '& .tool-icon': {
                color: theme.palette.common.white,
                transform: 'scale(1.1)',
            },
            '& .tool-title': {
                color: primaryColor,
            },
        } : {},
        '&:active': available ? {
            transform: 'translateY(0px)', // Pressed down effect
            // Reduced shadow on active - Google/Microsoft style
            boxShadow: isDarkMode
                ? `0 1px 2px ${alpha(theme.palette.common.black, 0.4)}, 0 2px 4px ${alpha(theme.palette.common.black, 0.3)}`
                : `0 1px 2px ${alpha(theme.palette.common.black, 0.08)}, 0 2px 4px ${alpha(theme.palette.common.black, 0.06)}`,
        } : {},
        '& .card-content': {
            padding: theme.spacing(3, 2),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            flexGrow: 1,
            minHeight: '140px',
            [mobile]: {
                flexDirection: 'row',
                alignItems: 'center',
                textAlign: 'left',
                justifyContent: 'flex-start',
                padding: theme.spacing(2, 2),
                minHeight: 'auto',
                gap: theme.spacing(2),
            },
        },
        '& .icon-wrapper': {
            width: '56px',
            height: '56px',
            borderRadius: theme.spacing(1.5),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: theme.spacing(2),
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            flexShrink: 0,
            // Icon on left for horizontal layout
            [mobile]: {
                width: '48px',
                height: '48px',
                marginBottom: 0,
                marginRight: 0,
            },
        },
        '& .tool-icon': {
            fontSize: '1.75rem',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            zIndex: 1,
            // Icon size for horizontal layout
            [theme.breakpoints.down('sm')]: {
                fontSize: '1.5rem',
            },
        },
        '& .tool-title': {
            fontSize: '0.9375rem',
            fontWeight: theme.typography.fontWeightBold,
            color: theme.palette.text.primary,
            lineHeight: 1.4,
            margin: 0,
            marginBottom: theme.spacing(0.5),
            transition: 'color 0.3s ease',
            // Title for horizontal layout
            [theme.breakpoints.down('sm')]: {
                fontSize: '0.9375rem',
                lineHeight: 1.3,
                marginBottom: theme.spacing(0.25),
            },
        },
        '& .tool-description': {
            fontSize: '0.75rem',
            color: theme.palette.text.secondary,
            lineHeight: 1.5,
            marginTop: theme.spacing(0.5),
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            // Show mini description for horizontal layout
            [theme.breakpoints.down('sm')]: {
                fontSize: '0.75rem',
                lineHeight: 1.4,
                marginTop: 0,
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
        },
        '& .card-actions': {
            padding: theme.spacing(1.5, 2),
            background: isDarkMode
                ? alpha(theme.palette.common.white, 0.03)
                : alpha(theme.palette.common.black, 0.02),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(1),
            minHeight: '48px',
            borderTop: `1px solid ${isDarkMode ? alpha(theme.palette.common.white, 0.05) : alpha(theme.palette.common.black, 0.05)}`,
            // Hide category bar on mobile for horizontal layout
            [mobile]: {
                display: 'none',
            },
        },
        // Category-specific styling
        '& .card-actions.category-developer-tools': {
            borderTopColor: theme.palette.info.main,
            background: isDarkMode
                ? alpha(theme.palette.info.main, 0.2)
                : alpha(theme.palette.info.main, 0.1),
        },
        '& .card-actions.category-text-processing': {
            borderTopColor: theme.palette.secondary.main,
            background: isDarkMode
                ? alpha(theme.palette.secondary.main, 0.1)
                : alpha(theme.palette.secondary.main, 0.05),
        },
        '& .card-actions.category-digital-marketing': {
            borderTopColor: theme.palette.success.main,
            background: isDarkMode
                ? alpha(theme.palette.success.main, 0.1)
                : alpha(theme.palette.success.main, 0.05),
        },
        '& .card-actions.category-online-calculators': {
            borderTopColor: theme.palette.warning.main,
            background: isDarkMode
                ? alpha(theme.palette.warning.main, 0.1)
                : alpha(theme.palette.warning.main, 0.05),
        },
        '& .card-actions.category-design-tools': {
            borderTopColor: theme.palette.error.main,
            background: isDarkMode
                ? alpha(theme.palette.error.main, 0.1)
                : alpha(theme.palette.error.main, 0.05),
        },
        '& .card-actions.category-converters': {
            borderTopColor: '#9c27b0', // Purple
            background: isDarkMode
                ? alpha('#9c27b0', 0.1)
                : alpha('#9c27b0', 0.05),
        },
        '& .card-actions.category-url-tools': {
            borderTopColor: '#00bcd4', // Cyan
            background: isDarkMode
                ? alpha('#00bcd4', 0.1)
                : alpha('#00bcd4', 0.05),
        },
        '& .tool-category': {
            fontSize: '0.75rem',
            color: theme.palette.text.secondary,
            fontWeight: theme.typography.fontWeightMedium,
        },
        '& .tool-stats': {
            fontSize: '0.75rem',
            color: theme.palette.text.secondary,
            fontWeight: theme.typography.fontWeightMedium,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
        },
        '& .favorite-button': {
            // Desktop: top-right absolute
            [theme.breakpoints.up('sm')]: {
                position: 'absolute',
                top: theme.spacing(1),
                right: theme.spacing(1),
                zIndex: 10,
                padding: theme.spacing(0.5),
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                backdropFilter: 'blur(4px)',
                '&:hover': {
                    backgroundColor: theme.palette.background.paper,
                },
            },
            // Mobile: right side in flex layout
            [mobile]: {
                position: 'relative',
                top: 'auto',
                right: 'auto',
                backgroundColor: 'transparent',
                backdropFilter: 'none',
                marginLeft: 'auto',
                flexShrink: 0,
                padding: theme.spacing(0.5),
            },
        },
        // Horizontal variant
        '&.variant-horizontal .card-content': {
            flexDirection: 'row',
            alignItems: 'center',
            textAlign: 'left',
            justifyContent: 'flex-start',
            padding: theme.spacing(2.5, 3),
            gap: theme.spacing(2),
            minHeight: 'auto',
            [mobile]: {
                padding: theme.spacing(2, 2),
                gap: theme.spacing(1.5),
            },
        },
        '&.variant-horizontal .icon-wrapper': {
            marginBottom: 0,
            marginRight: theme.spacing(2),
            [mobile]: {
                marginRight: theme.spacing(1.5),
                width: '48px',
                height: '48px',
            },
        },
        '&.variant-horizontal .tool-title': {
            textAlign: 'left',
            marginBottom: theme.spacing(0.25),
        },
        '&.variant-horizontal .tool-description': {
            textAlign: 'left',
            WebkitLineClamp: 2,
            [mobile]: {
                WebkitLineClamp: 1,
            },
        },
        '&.variant-horizontal .card-actions': {
            display: 'none',
        },
        '&.variant-horizontal .favorite-button': {
            [mobile]: {
                marginLeft: 'auto',
            },
        },

        // Square variant
        '&.variant-square .card-content': {
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: theme.spacing(2.25),
            gap: theme.spacing(1.25),
            minHeight: 'auto',
            [mobile]: {
                padding: theme.spacing(1.75),
                gap: theme.spacing(1),
                aspectRatio: '1 / 1',
            },
        },
        '&.variant-square .icon-wrapper': {
            margin: 0,
            marginBottom: theme.spacing(1.25),
            width: '56px',
            height: '56px',
            [mobile]: {
                width: '52px',
                height: '52px',
            },
        },
        '&.variant-square .tool-title': {
            textAlign: 'center',
            marginBottom: theme.spacing(0.5),
            [mobile]: {
                fontSize: '0.95rem',
                lineHeight: 1.35,
            },
        },
        '&.variant-square .tool-description': {
            display: 'none',
        },
        '&.variant-square .card-actions': {
            display: 'none',
        },
        '&.variant-square .favorite-button': {
            position: 'absolute',
            top: theme.spacing(1),
            right: theme.spacing(1),
            marginLeft: 0,
            backgroundColor: alpha(theme.palette.background.paper, isDarkMode ? 0.2 : 0.8),
            backdropFilter: 'blur(4px)',
            borderRadius: '50%',
            [mobile]: {
                top: theme.spacing(1),
                right: theme.spacing(1),
            },
        },

        // Vertical variant
        '&.variant-vertical .card-content': {
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            gap: theme.spacing(1.5),
            minHeight: '160px',
            padding: theme.spacing(3),
            [mobile]: {
                padding: theme.spacing(2),
                gap: theme.spacing(1),
                flexDirection: 'column',
                textAlign: 'center',
            },
        },
        '&.variant-vertical .card-actions': {
            display: 'flex',
            [mobile]: {
                display: 'flex',
            },
        },
    };
});

// Helper function to convert category name to className
const getCategoryClassName = (category) => {
    if (!category) return '';
    return `category-${category.toLowerCase().replace(/\s+/g, '-')}`;
};

// Helper function to get category color
const getCategoryColor = (category, theme) => {
    if (!category) return theme.palette.primary.main;

    const categoryLower = category.toLowerCase().replace(/\s+/g, '-');

    switch (categoryLower) {
        case 'developer-tools':
            return theme.palette.info.main;
        case 'text-processing':
            return theme.palette.secondary.main;
        case 'digital-marketing':
            return theme.palette.success.main;
        case 'online-calculators':
            return theme.palette.warning.main;
        case 'design-tools':
            return theme.palette.error.main;
        case 'converters':
            return '#9c27b0'; // Purple
        case 'url-tools':
            return '#00bcd4'; // Cyan
        case 'petroleum-engineering':
            return '#ff5722'; // Deep Orange
        default:
            return theme.palette.primary.main;
    }
};

export const ToolCard = ({ tool, hideStatus = false, variant = 'default', className }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const auth = useSelector((store) => store.auth);
    const favorites = auth?.user?.favorites || [];
    const isFavorite = favorites.includes(tool.id);

    const handleClick = (e) => {
        // Don't trigger card click if clicking on favorite button
        if (e.target.closest('.favorite-button')) {
            return;
        }
        if (tool.available) {
            // Set tool in Redux store before navigation
            dispatch(setSelectedTool(tool));
            dispatch(setActiveMenu(tool.id));
            navigate(tool.route);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && tool.available) {
            e.preventDefault();
            // Set tool in Redux store before navigation
            dispatch(setSelectedTool(tool));
            dispatch(setActiveMenu(tool.id));
            navigate(tool.route);
        }
    };

    const handleFavoriteClick = (e) => {
        e.stopPropagation();
        dispatch(toggleFavorite(tool.id));
    };

    const categoryClassName = getCategoryClassName(tool.category);
    const categoryColor = getCategoryColor(tool.category, theme);
    const isDarkMode = theme.palette.mode === 'dark';

    const variantClass = `variant-${variant}`;
    const combinedClassName = className ? `${variantClass} ${className}` : variantClass;

    return (
        <ToolCardStyled
            className={combinedClassName}
            available={tool.available}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            role={tool.available ? "button" : undefined}
            tabIndex={tool.available ? 0 : -1}
            aria-label={`${tool.title} tool${!tool.available ? ' (coming soon)' : ''}`}
            elevation={0}
        >
            <CardContent className="card-content">
                {/* Icon on left */}
                <Box
                    className="icon-wrapper"
                    sx={{
                        background: isDarkMode
                            ? alpha(categoryColor, 0.2)
                            : alpha(categoryColor, 0.1),
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            borderRadius: theme.spacing(1.5),
                            background: `linear-gradient(135deg, ${categoryColor}, ${alpha(categoryColor, 0.6)})`,
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                        },
                        '&:hover::after': tool.available ? {
                            opacity: 1,
                        } : {},
                        '& .tool-icon': {
                            color: categoryColor,
                        },
                    }}
                >
                    {React.createElement(tool.icon, {
                        className: 'tool-icon'
                    })}
                </Box>

                {/* Title and Description in middle */}
                <Box sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    <Typography component="h3" variant="h6" className="tool-title">
                        {tool.title}
                    </Typography>
                    {tool.description && (
                        <Typography variant="body2" className="tool-description">
                            {tool.description}
                        </Typography>
                    )}
                </Box>

                {/* Favorite button on right (mobile) */}
                <IconButton
                    className="favorite-button"
                    onClick={handleFavoriteClick}
                    size="small"
                    aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                    sx={{
                        color: isFavorite ? theme.palette.warning.main : theme.palette.text.secondary,
                        '&:hover': {
                            color: theme.palette.warning.main,
                            backgroundColor: alpha(theme.palette.warning.main, 0.1),
                        },
                    }}
                >
                    {isFavorite ? (
                        <StarIcon sx={{ fontSize: '1.25rem' }} />
                    ) : (
                        <StarBorderIcon sx={{ fontSize: '1.25rem' }} />
                    )}
                </IconButton>
            </CardContent>
            <CardActions className={`card-actions ${categoryClassName}`}>
                {tool.category && (
                    <Typography variant="body2" className="tool-category">
                        {tool.category}
                    </Typography>
                )}
                {!hideStatus && (
                    <Typography variant="body2" className="tool-stats">
                        {tool.available ? (
                            <>
                                <CheckCircleIcon sx={{ fontSize: '0.875rem', color: theme.palette.success.main }} />
                                <span>Available</span>
                            </>
                        ) : (
                            <span>Coming Soon</span>
                        )}
                    </Typography>
                )}
            </CardActions>
        </ToolCardStyled>
    );
};

